/**
 * Form sections shared by the specialist and consultant forms:
 * contact preferences, coverage areas, clinic and usual weekly working hours.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContactPhone as ContactIcon,
  Place as PlaceIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CONTACT_METHODS, type ContactMethod } from '../../store/api/patientsApi';
import { useGetProviderFilterOptionsQuery } from '../../store/api/providersApi';
import type { ProviderProfile, WeeklyScheduleEntry } from '../../types/providerProfile';
import { DEFAULT_WORKING_HOURS, WEEK_DAYS, cleanPlaces } from '../../utils/providerProfile';

interface ProviderProfileFieldsProps {
  value: ProviderProfile;
  onChange: (next: ProviderProfile) => void;
  /** field → translated message */
  errors?: Record<string, string>;
}

const sectionTitleSx = { mb: 3, display: 'flex', alignItems: 'center', gap: 1 };
const fieldsGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
  gap: 3,
};

const ProviderProfileFields = ({ value, onChange, errors = {} }: ProviderProfileFieldsProps) => {
  const { t } = useTranslation();
  const { data: placesData } = useGetProviderFilterOptionsQuery();
  const places = placesData?.data;

  const set = <K extends keyof ProviderProfile>(key: K, next: ProviderProfile[K]) =>
    onChange({ ...value, [key]: next });

  const entryFor = (day: number) => value.weekly_schedule.find((entry) => entry.day === day);

  const setDay = (day: number, entry: WeeklyScheduleEntry | null) => {
    const others = value.weekly_schedule.filter((item) => item.day !== day);
    set('weekly_schedule', entry ? [...others, entry] : others);
  };

  const placesField = (
    field: 'service_cities' | 'service_areas',
    options: string[] | undefined,
    label: string,
  ) => (
    <Autocomplete
      multiple
      freeSolo
      autoSelect
      options={options ?? []}
      value={value[field]}
      onChange={(_e, next) => set(field, cleanPlaces(next))}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={tag} size="small" {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={t('providers.form.placesHelper')} />
      )}
    />
  );

  return (
    <>
      {/* Contact preferences */}
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={sectionTitleSx}>
          <ContactIcon color="primary" />
          {t('providers.sections.contact')}
        </Typography>
        <Box sx={fieldsGridSx}>
          <Box>
            <FormControl fullWidth>
              <InputLabel>{t('providers.form.contactMethod')}</InputLabel>
              <Select
                label={t('providers.form.contactMethod')}
                value={value.preferred_contact_method}
                onChange={(e) => set('preferred_contact_method', e.target.value as ContactMethod | '')}
              >
                <MenuItem value="">
                  <em>{t('providers.form.notSpecified')}</em>
                </MenuItem>
                {CONTACT_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>
                    {t(`users.contactMethods.${method}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <TextField
              fullWidth
              label={t('providers.form.contactTime')}
              value={value.preferred_contact_time}
              onChange={(e) => set('preferred_contact_time', e.target.value)}
              helperText={t('providers.form.contactTimeHelper')}
              inputProps={{ maxLength: 100 }}
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('providers.form.contactNotes')}
              value={value.contact_notes}
              onChange={(e) => set('contact_notes', e.target.value)}
            />
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Coverage & clinic */}
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={sectionTitleSx}>
          <PlaceIcon color="primary" />
          {t('providers.sections.coverage')}
        </Typography>
        <Box sx={fieldsGridSx}>
          <Box>
            {placesField('service_cities', places?.cities, t('providers.form.cities'))}
          </Box>
          <Box>
            {placesField('service_areas', places?.areas, t('providers.form.areas'))}
          </Box>
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('providers.form.clinicAddress')}
              value={value.clinic_address}
              onChange={(e) => set('clinic_address', e.target.value)}
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              label={t('providers.form.clinicMapLink')}
              value={value.clinic_location_url}
              onChange={(e) => set('clinic_location_url', e.target.value)}
              placeholder="https://maps.google.com/..."
              error={!!errors.clinic_location_url}
              helperText={errors.clinic_location_url || t('providers.form.clinicMapLinkHelper')}
              inputProps={{ dir: 'ltr', maxLength: 500 }}
            />
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Usual working hours */}
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ ...sectionTitleSx, mb: 1 }}>
          <ScheduleIcon color="primary" />
          {t('providers.sections.schedule')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('providers.form.scheduleNote')}
        </Typography>
        {errors.weekly_schedule && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.weekly_schedule}
          </Alert>
        )}
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {WEEK_DAYS.map((day) => {
            const entry = entryFor(day);
            const invalid = !!entry && (!entry.from || !entry.to || entry.from >= entry.to);
            return (
              <Box
                key={day}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: '180px 160px 160px' },
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <FormControlLabel
                  sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, m: 0 }}
                  control={
                    <Checkbox
                      checked={!!entry}
                      onChange={(e) =>
                        setDay(day, e.target.checked ? { day, ...DEFAULT_WORKING_HOURS } : null)
                      }
                    />
                  }
                  label={t(`providers.days.${day}`)}
                />
                <TextField
                  type="time"
                  size="small"
                  label={t('providers.form.from')}
                  value={entry?.from ?? ''}
                  onChange={(e) => entry && setDay(day, { ...entry, from: e.target.value })}
                  disabled={!entry}
                  error={invalid}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="time"
                  size="small"
                  label={t('providers.form.to')}
                  value={entry?.to ?? ''}
                  onChange={(e) => entry && setDay(day, { ...entry, to: e.target.value })}
                  disabled={!entry}
                  error={invalid}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default ProviderProfileFields;
